import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Unlink,
  Check,
} from "lucide-react";

const GROUPS = [
  [
    { cmd: "bold", Icon: Bold, label: "Bold (Ctrl+B)" },
    { cmd: "italic", Icon: Italic, label: "Italic (Ctrl+I)" },
    { cmd: "underline", Icon: Underline, label: "Underline (Ctrl+U)" },
  ],
  [
    { cmd: "justifyLeft", Icon: AlignLeft, label: "Align left" },
    { cmd: "justifyCenter", Icon: AlignCenter, label: "Align center" },
    { cmd: "justifyRight", Icon: AlignRight, label: "Align right" },
  ],
];
const ALL_CMDS = GROUPS.flat().map((c) => c.cmd);

function selectionAnchor(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  let node = sel.getRangeAt(0).commonAncestorContainer;
  if (node.nodeType === 3) node = node.parentNode;
  const a = node.closest && node.closest("a");
  return a && root.contains(a) ? a : null;
}

export default function FormatToolbar({ targetRef, onDone }) {
  const [pos, setPos] = useState(null);
  const [active, setActive] = useState({});
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [editingLink, setEditingLink] = useState(false);
  const savedRange = useRef(null);
  const inputRef = useRef(null);

  const update = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top - 44, left: r.left });
    const ae = document.activeElement;
    if (ae && ae.closest("[data-format-toolbar]")) return;
    const next = {};
    ALL_CMDS.forEach((c) => (next[c] = document.queryCommandState(c)));
    next.link = !!selectionAnchor(el);
    setActive(next);
  }, [targetRef]);

  useEffect(() => {
    update();
    document.addEventListener("selectionchange", update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("selectionchange", update);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  useEffect(() => {
    if (linkOpen && inputRef.current) inputRef.current.focus();
  }, [linkOpen]);

  const restoreSelection = () => {
    targetRef.current && targetRef.current.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const openLink = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    savedRange.current = sel.getRangeAt(0).cloneRange();
    const a = selectionAnchor(targetRef.current);
    setEditingLink(!!a);
    setUrl(a ? a.getAttribute("href") : "https://");
    setLinkOpen(true);
  };

  const applyLink = () => {
    const href = url.trim();
    restoreSelection();
    if (href && href !== "https://") {
      const a = selectionAnchor(targetRef.current);
      if (a) {
        a.setAttribute("href", href);
      } else if (savedRange.current && savedRange.current.collapsed) {
        document.execCommand(
          "insertHTML",
          false,
          `<a href="${href}" target="_blank" rel="noopener noreferrer">${href}</a>`
        );
      } else {
        document.execCommand("createLink", false, href);
        targetRef.current.querySelectorAll(`a[href="${href}"]`).forEach((el) => {
          el.setAttribute("target", "_blank");
          el.setAttribute("rel", "noopener noreferrer");
        });
      }
    }
    setLinkOpen(false);
    update();
  };

  const removeLink = () => {
    restoreSelection();
    const a = selectionAnchor(targetRef.current);
    if (a) {
      const range = document.createRange();
      range.selectNode(a);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
    document.execCommand("unlink");
    setLinkOpen(false);
    update();
  };

  const leavingToolbar = (e) =>
    !(e.relatedTarget && (e.relatedTarget.closest("[data-format-toolbar]") || e.relatedTarget === targetRef.current));

  if (!pos) return null;

  const btnCls = (on) =>
    `flex h-7 w-7 items-center justify-center rounded transition-colors ${
      on ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-100"
    }`;

  return createPortal(
    <div
      data-testid="format-toolbar"
      data-format-toolbar="true"
      className="fixed z-[70] flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
      style={{ top: Math.max(8, pos.top), left: Math.max(8, pos.left) }}
      onMouseDown={(e) => {
        if (e.target.tagName !== "INPUT") e.preventDefault();
      }}
    >
      {GROUPS.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <span className="mx-0.5 h-5 w-px bg-slate-200" />}
          {group.map(({ cmd, Icon, label }) => (
            <button
              key={cmd}
              type="button"
              data-testid={`format-${cmd}-btn`}
              title={label}
              aria-pressed={!!active[cmd]}
              onMouseDown={(e) => {
                e.preventDefault();
                document.execCommand(cmd);
                update();
              }}
              className={btnCls(active[cmd])}
            >
              <Icon size={14} />
            </button>
          ))}
        </React.Fragment>
      ))}
      <span className="mx-0.5 h-5 w-px bg-slate-200" />
      <button
        type="button"
        data-testid="format-link-btn"
        title={active.link ? "Edit link" : "Add link"}
        aria-pressed={!!active.link}
        onMouseDown={(e) => {
          e.preventDefault();
          linkOpen ? setLinkOpen(false) : openLink();
        }}
        className={btnCls(active.link || linkOpen)}
      >
        <LinkIcon size={14} />
      </button>
      {linkOpen && (
        <div className="ml-1 flex items-center gap-1" data-testid="format-link-editor">
          <input
            ref={inputRef}
            data-testid="format-link-input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setLinkOpen(false);
                restoreSelection();
              }
            }}
            onBlur={(e) => {
              if (leavingToolbar(e)) {
                setLinkOpen(false);
                onDone();
              }
            }}
            placeholder="https://example.com"
            className="h-7 w-56 rounded border border-slate-300 px-2 text-xs text-slate-800 outline-none focus:border-slate-500"
          />
          <button
            type="button"
            data-testid="format-link-apply-btn"
            title="Apply link"
            onMouseDown={(e) => {
              e.preventDefault();
              applyLink();
            }}
            className={btnCls(false)}
          >
            <Check size={14} />
          </button>
          {(active.link || editingLink) && (
            <button
              type="button"
              data-testid="format-link-remove-btn"
              title="Remove link"
              onMouseDown={(e) => {
                e.preventDefault();
                removeLink();
              }}
              className={btnCls(false)}
            >
              <Unlink size={14} />
            </button>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}
